import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";

// Watches for a newer deployed build and gets the browser onto it.
//
// The problem this solves: nginx serves index.html without a Cache-Control header, so
// browsers keep re-using the cached copy — and with it the old hashed bundle names. A
// terminal that was opened days ago, or one whose index.html is still in cache, keeps
// running last week's dashboard while a colleague on a fresh cache runs today's.
//
// asset-manifest.json is written on every build and names the current main chunk, so
// fetching it with cache: "no-store" and comparing against the chunk this page actually
// loaded tells us whether we are stale.
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const RELOADED_FOR_KEY = "appUpdateReloadedFor";

// The script tag CRA emits for the entry chunk, e.g. /static/js/main.48b6d902.chunk.js.
const runningMainChunk = () => {
    const scripts = Array.from(document.querySelectorAll("script[src]"));
    const main = scripts
        .map((s) => s.getAttribute("src") || "")
        .find((src) => /\/static\/js\/main\.[^/]+\.js$/.test(src));
    return main || null;
};

const deployedMainChunk = async () => {
    // The query string defeats any intermediate cache that ignores no-store.
    const response = await fetch(`/asset-manifest.json?ts=${Date.now()}`, { cache: "no-store" });
    if (!response.ok) return null;
    const manifest = await response.json();
    return (manifest && manifest.files && manifest.files["main.js"]) || null;
};

// The POS till holds an unsaved basket, so it is never reloaded from under the operator —
// they get the bar and reload when the counter is clear. Everywhere else refreshes itself.
const holdsUnsavedWork = () => window.location.pathname.startsWith("/admin/pos");

function AppUpdateWatcher() {
    const [updateReady, setUpdateReady] = useState(false);
    const running = useRef(runningMainChunk());

    const check = useCallback(async () => {
        try {
            const deployed = await deployedMainChunk();
            if (!deployed || !running.current || deployed === running.current) {
                return;
            }
            // Reload at most once per deployed version: if index.html is still being served
            // from cache the reload cannot help, and looping would make the app unusable.
            if (!holdsUnsavedWork() && sessionStorage.getItem(RELOADED_FOR_KEY) !== deployed) {
                sessionStorage.setItem(RELOADED_FOR_KEY, deployed);
                window.location.reload();
                return;
            }
            setUpdateReady(true);
        } catch (error) {
            // Offline or the manifest is unreachable — try again on the next tick.
            console.error("Update check failed:", error);
        }
    }, []);

    useEffect(() => {
        check();
        const timer = setInterval(check, POLL_INTERVAL_MS);
        // A till is often left untouched for hours; checking when the tab is looked at
        // again catches a deploy sooner than the interval alone.
        const onVisible = () => {
            if (document.visibilityState === "visible") check();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [check]);

    if (!updateReady) return null;

    return (
        <Box
            style={{
                position: "fixed",
                left: "50%",
                bottom: 24,
                transform: "translateX(-50%)",
                zIndex: 2000,
                display: "flex",
                alignItems: "center",
                gap: 16,
                padding: "10px 16px",
                borderRadius: 8,
                background: "#32325d",
                color: "#fff",
                boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            }}
        >
            <Typography variant="body2" style={{ color: "#fff", fontWeight: 600 }}>
                A new version of the dashboard is available.
            </Typography>
            <Button
                size="small"
                variant="contained"
                color="primary"
                style={{ textTransform: "none", fontWeight: 600 }}
                onClick={() => window.location.reload()}
            >
                Update Now
            </Button>
        </Box>
    );
}

export default AppUpdateWatcher;
